import React, {useState, useEffect, useMemo} from 'react';
import {RecyclingChart} from "@/pages/Recycle/recycling-chart";
import TreesEquivalent from './TreesEquivalent';
import RecyclabilityScore from './RecyclabilityScore';
import WasteDiverted from './WasteDiverted';
import {WasteImpactCard} from './WasteImpact.jsx';
import CO2SavingsChart from '../CO2savings/co2';
import axiosInstance from '@/api/axios';
import {GHGAvertedCard} from './GHGaverted';
import {RecyclingHeader} from './siteheader';

export default function RecyclingDashboard() {
    const [year, setYear] = useState(new Date().getFullYear());
    const [selectedBins, setSelectedBins] = useState([]);
    const [selectedWasteType, setSelectedWasteType] = useState('organic');
    const [selectedTimePeriod, setSelectedTimePeriod] = useState('year');
    const [isFetching, setIsFetching] = useState(false);
    const [apiData, setApiData] = useState(null);
    const [wasteDivertedData, setWasteDivertedData] = useState(null);
    const [environmentalImpactData, setEnvironmentalImpactData] = useState(null);
    const [availableBins, setAvailableBins] = useState([]);

    // Fetch bin IDs dynamically
    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                console.log("Layout: Fetching bin IDs from API");
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                setAvailableBins(binIds);
                
                // Set default selected bin to the first available bin
                if (binIds.length > 0 && selectedBins.length === 0) {
                    setSelectedBins([binIds[0]]);
                }
            } catch (error) {
                console.error("Layout: Error fetching bin IDs:", error);
            }
        };

        fetchBinIds();
    }, []); // No dependencies to avoid re-fetching unnecessarily


    const fetchRecyclingData = async () => {
        // Don't fetch if no bins are selected yet
        if (selectedBins.length === 0) return;
        
        setIsFetching(true);
        try {
            const response = await axiosInstance.get('recycling_rate', {
                params: {
                    period: selectedTimePeriod,
                    year: selectedTimePeriod === 'year' ? year : undefined,
                    'bins[]': selectedBins
                }
            });
            setApiData(response.data);
        } catch (error) {
            console.error("Error fetching recycling data:", error);
            setApiData({
                recycling_rate: 0,
                historical_data: [],
                period: selectedTimePeriod,
                year: year
            });
        } finally {
            setIsFetching(false);
        }
    };

    const fetchWasteDivertedData = async () => {
        // Don't fetch if no bins are selected yet
        if (selectedBins.length === 0) return;
        
        try {
            const params = new URLSearchParams();
            params.append('period', selectedTimePeriod);
            if (selectedTimePeriod === 'year') {
                params.append('year', year);
            }
            selectedBins.forEach(bin => params.append('bins[]', bin));

            const response = await axiosInstance.get('waste-diverted', {params});
            setWasteDivertedData(response.data);
        } catch (error) {
            console.error("Error fetching waste diverted data:", error);
            setWasteDivertedData({
                waste_diverted: 0,
                total_waste: 0,
                historical_data: [],
                period: selectedTimePeriod,
                start_date: new Date().toISOString(),
                end_date: new Date().toISOString(),
                change_rate: 0
            });
        }
    };

    const fetchEnvironmentalImpactData = async () => {
        // Don't fetch if no bins are selected yet
        if (selectedBins.length === 0) return;
        
        try {
            const params = {
                period: selectedTimePeriod,
                'bins[]': selectedBins
            };

            // Only include year parameter if period is 'year'
            if (selectedTimePeriod === 'year') {
                params.year = year;
            }

            const response = await axiosInstance.get('enviromental_impact', {params});
            setEnvironmentalImpactData(response.data);
        } catch (error) {
            console.error("Error fetching environmental impact data:", error);
            setEnvironmentalImpactData({
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            });
        }
    };

    useEffect(() => {
        fetchRecyclingData();
        fetchWasteDivertedData();
        fetchEnvironmentalImpactData();
    }, [year, selectedBins, selectedTimePeriod]);

    const dashboardData = useMemo(() => {
        const defaultData = {
            recycling_rate: 0,
            historical_data: [],
            period: selectedTimePeriod,
            year: year
        };

        const data = apiData || defaultData;
        const recyclingRate = data.recycling_rate || 0;

        return {
            treesEquivalent: (recyclingRate * 0.5).toFixed(1),
            recyclabilityScore: 0, // Let RecyclabilityScore component fetch its own data
            wasteDiverted: {
                amount: (recyclingRate * 100).toFixed(0),
                percentage: recyclingRate.toFixed(1)
            },
            recyclingRate,
            historicalData: data.historical_data || [],
            period: data.period,
            year: data.year,
            environmentalImpact: environmentalImpactData || {
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            }
        };
    }, [apiData, environmentalImpactData, year, selectedTimePeriod]);

    return (
        <>
            <RecyclingHeader
                selectedBins={selectedBins}
                setSelectedBins={setSelectedBins}
                selectedWasteType={selectedWasteType}
                setSelectedWasteType={setSelectedWasteType}
                selectedTimePeriod={selectedTimePeriod}
                setSelectedTimePeriod={setSelectedTimePeriod}
                year={year}
                setYear={setYear}
                allBins={availableBins}
            />
            <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">
                {/* Main Dashboard Grid */}
                <div className="space-y-6 pb-8 pt-4">
                    {isFetching || selectedBins.length === 0 ? (
                        <div className="flex items-center justify-center h-64">
                            <p>Loading data...</p>
                        </div>
                    ) : (
                        <>
                            {/* First Row - 3 charts */}
                            <div
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-4 sm:pt-0 items-start">
                                <div className="min-h-[200px]">
                                    <RecyclingChart data={{
                                        recycling_rate: dashboardData.recyclingRate,
                                        historical_data: dashboardData.historicalData,
                                        period: dashboardData.period,
                                        year: dashboardData.year
                                    }}/>
                                </div>

                                <div className="min-h-[200px]">
                                    <TreesEquivalent
                                        selectedBins={selectedBins}
                                        selectedPeriod={selectedTimePeriod}
                                        selectedYear={year}
                                    />
                                </div>

                                <div className="min-h-[200px]">
                                    <RecyclabilityScore
                                        year={dashboardData.year}
                                        bins={selectedBins}
                                        period={dashboardData.period}
                                    />
                                </div>

                                <div className="min-h-[200px] md:col-span-1">
                                    <WasteDiverted
                                        selectedBins={selectedBins}
                                        selectedTimePeriod={selectedTimePeriod}
                                        year={year}
                                        data={wasteDivertedData}
                                    />
                                </div>
                            </div>

                            {/* Third Row - Waste Metrics */}
                            {/* Third Row - Waste Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-4 sm:pt-0 items-start">
                                <div className="w-full">
                                    <WasteImpactCard
                                        bins={selectedBins}
                                        period={selectedTimePeriod}
                                        year={year}
                                    />
                                </div>

                              <div className="w-full">
    <CO2SavingsChart
        id={selectedBins[0] || 'R4'}
        wasteitem={selectedWasteType}
        dayvalue={selectedTimePeriod === 'year' ? year : selectedTimePeriod}
        selectedButton_={selectedTimePeriod === 'custom' ? 'custom' : 'day'}
        datevalue={[{startDate: null, endDate: null, key: 'selection'}]}
        co2Data={dashboardData.environmentalImpact}
    />
</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}